/* eslint-disable react-hooks/refs */
import classNames from 'classnames';

import Icon, { IconSize, type SvgIcon } from '@helsenorge/designsystem-react/components/Icon';
import X from '@helsenorge/designsystem-react/components/Icons/X';
import { usePseudoClasses } from '@helsenorge/designsystem-react/hooks/usePseudoClasses';
import { palette } from '@helsenorge/designsystem-react/theme/palette';

import styles from './styles.module.scss';

interface Props extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  icon: SvgIcon;
  isActive?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
  afterIconChildren?: React.ReactNode;
  htmlMarkup?: 'button' | 'a';
  href?: string;
  testId?: string;
  onMouseEnter?: () => void;
  onFocus?: () => void;
  ref?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
}

export const HeaderButton: React.FC<Props> = props => {
  const { icon, isActive, onClick, children, testId, htmlMarkup = 'button' } = props;
  const { refObject: hoverRef, isHovered } = usePseudoClasses<HTMLButtonElement | HTMLAnchorElement | null>(props.ref);

  const headerButtonClasses = classNames(styles['header-button'], {
    [styles['header-button--active']]: isActive,
  });
  const headerButtonTextClasses = classNames(styles['header-button__text'], {
    [styles['header-button__text--active']]: isActive,
  });

  const contents = (
    <>
      <span className={styles['header-button__icon']}>
        <Icon size={IconSize.XSmall} svgIcon={isActive ? X : icon} color={palette.black} isHovered={isHovered} />
        {props.afterIconChildren}
      </span>
      <span className={headerButtonTextClasses}>{children}</span>
    </>
  );

  return (
    <>
      {htmlMarkup === 'button' && (
        <button
          ref={hoverRef as React.RefObject<HTMLButtonElement>}
          className={headerButtonClasses}
          onClick={(e): void => onClick && onClick(e)}
          aria-expanded={isActive}
          data-testid={`header-button-${testId}`}
          aria-label={props['aria-label']}
          onMouseEnter={props.onMouseEnter}
          onFocus={props.onFocus}
        >
          {contents}
        </button>
      )}
      {htmlMarkup === 'a' && (
        <a
          ref={hoverRef as React.RefObject<HTMLAnchorElement>}
          className={headerButtonClasses}
          href={props.href}
          data-testid={`header-button-${testId}`}
          aria-label={props['aria-label']}
        >
          {contents}
        </a>
      )}
    </>
  );
};

export default HeaderButton;
